import { useEffect, useRef } from 'react';
import { MysteryShopperClient } from '../api/api.g';
import { UserPrefsHelper } from '../models/helpers/userPrefsHelper';
import { UserPreferencesState, userPrefsActions } from '../store/userPrefsSlice';
import { store } from '../store/store';
import useApi from './api-hook';
import { useAppDispatch } from './store-hook';
import { useMutation } from '@tanstack/react-query';
import { useProfileCompletionBonus } from './useProfileCompletionBonus';

const useUserPrefsService = () => {
  const bonus = useProfileCompletionBonus();
  const dispatch = useAppDispatch();
  const apiMysteryShopper = useApi(MysteryShopperClient);
  const callOnUpdateDoneRef = useRef<(pref?) => void>(undefined);
  const callOnUpdateFailedRef = useRef<(() => void) | undefined>(undefined);

  const saveBasicInfo = async (onUpdateDone?: () => void) => {
    const latestUserPrefs = store.getState().userPrefs as UserPreferencesState;
    const data = UserPrefsHelper.mapToSaveBasicInformationDto(latestUserPrefs);

    try {
      // Updating the DB
      await apiMysteryShopper.saveBasicInformation(data);
    } catch (error) {
      // Handle API Error
    } finally {
      onUpdateDone && onUpdateDone();
    }
  };

  const getMysteryShopperProfileMutation = useMutation({
    mutationFn: () => {
      return apiMysteryShopper.getProfile();
    },
    onError: () => {
      const failed = callOnUpdateFailedRef.current;
      callOnUpdateFailedRef.current = undefined;
      callOnUpdateDoneRef.current = undefined;
      failed?.();
    },
  });

  useEffect(() => {
    if (getMysteryShopperProfileMutation.isSuccess) {
      const userProfile = getMysteryShopperProfileMutation.data;
      void bonus.refresh();
      const userPref = UserPrefsHelper.mapFromToDto<UserPreferencesState>(userProfile.mysteryShopper);
      dispatch(userPrefsActions.setBasicInfo(userPref));
      userPref.profileMembers = userProfile.availableProfileMembers;
      dispatch(userPrefsActions.setProfileMembers(userProfile.availableProfileMembers));

      const callOnUpdateDone = callOnUpdateDoneRef.current;
      if (callOnUpdateDone) {
        callOnUpdateDone(userPref);
        callOnUpdateDoneRef.current = undefined;
      }
      callOnUpdateFailedRef.current = undefined;
    }
  }, [getMysteryShopperProfileMutation.isSuccess, getMysteryShopperProfileMutation.data, dispatch]);

  const updateUserProfileInfo = async (onUpdateDone?: (pref?) => void, onUpdateFailed?: () => void) => {
    callOnUpdateDoneRef.current = onUpdateDone;
    callOnUpdateFailedRef.current = onUpdateFailed;
    getMysteryShopperProfileMutation.mutate();
  };

  return {
    saveBasicInfo: saveBasicInfo,
    updateUserProfileInfo: updateUserProfileInfo,
  };
};

export default useUserPrefsService;
